"use client";
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@/lib/context/user-context';

// Supported language codes matching useLanguageSettings
export type SupportedLanguage = 'en' | 'zh' | 'es' | 'ja' | 'ko' | 'fr' | 'de' | 'pt' | 'ru';

// Legacy language names for backward compatibility
const LEGACY_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
    'chinese': 'zh',
    'english': 'en',
};

// Map browser language codes to our supported languages
const BROWSER_LANG_MAP: Record<string, SupportedLanguage> = {
    'zh': 'zh',
    'zh-cn': 'zh',
    'zh-tw': 'zh',
    'es': 'es',
    'ja': 'ja',
    'ko': 'ko',
    'fr': 'fr',
    'de': 'de',
    'pt': 'pt',
    'ru': 'ru',
};

const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'zh', 'es', 'ja', 'ko', 'fr', 'de', 'pt', 'ru'];

interface LanguageContextType {
    language: string;
    setLanguage: (language: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
    children: React.ReactNode;
}

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Helper function to normalize language code
function normalizeLanguage(lang: string | null): SupportedLanguage {
    if (!lang) return DEFAULT_LANGUAGE;
    
    const lowerLang = lang.toLowerCase();
    
    // Check if it's a legacy language name
    if (LEGACY_LANGUAGE_MAP[lowerLang]) {
        return LEGACY_LANGUAGE_MAP[lowerLang];
    }
    
    // Check if it's already a supported language code
    if (SUPPORTED_LANGUAGES.includes(lowerLang as SupportedLanguage)) {
        return lowerLang as SupportedLanguage;
    }
    
    // Try to map browser language code
    const langPrefix = lowerLang.split('-')[0];
    if (BROWSER_LANG_MAP[langPrefix]) {
        return BROWSER_LANG_MAP[langPrefix];
    }
    
    // Default to English
    return DEFAULT_LANGUAGE;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
    // 始终使用 DEFAULT_LANGUAGE 初始化，确保服务端和客户端初始值一致
    // 避免 hydration 不匹配
    const [language, setLanguageState] = useState<string>(DEFAULT_LANGUAGE);
    const { user } = useUser();
    const hasInitialized = useRef(false);

    // Initialize language from localStorage, user's native language, or browser settings
    useEffect(() => {
        const initializeLanguage = async () => {
            // Skip if already initialized and we have a stored value
            if (hasInitialized.current) return;
            
            try {
                const stored = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
                const normalizedStored = normalizeLanguage(stored);
                
                // If we have a stored value (user has manually set it before), use it
                if (stored && normalizedStored !== DEFAULT_LANGUAGE) {
                    setLanguageState(normalizedStored);
                    hasInitialized.current = true;
                    // Migrate legacy values
                    if (stored === 'chinese' || stored === 'english') {
                        if (typeof window !== 'undefined') {
                            localStorage.setItem('language', normalizedStored);
                        }
                    }
                    return;
                }
                
                // If no stored value and user is logged in, try to use their native language setting
                if (user?.id && !stored) {
                    try {
                        const response = await fetch(`/api/user-settings?userId=${user.id}`);
                        if (response.ok) {
                            const settings = await response.json();
                            const nativeLang = settings.languageSetting;
                            if (nativeLang && SUPPORTED_LANGUAGES.includes(nativeLang as SupportedLanguage)) {
                                setLanguageState(nativeLang);
                                if (typeof window !== 'undefined') {
                                    localStorage.setItem('language', nativeLang);
                                }
                                hasInitialized.current = true;
                                return;
                            }
                        }
                    } catch (err) {
                        console.log('Could not load user native language, falling back to browser detection');
                    }
                }
                
                // If user is not logged in yet, or native language fetch failed, use browser language
                // But only if we haven't initialized yet
                if (!hasInitialized.current) {
                    const navLang = typeof navigator !== 'undefined' ? navigator.language : 'en';
                    const initial = normalizeLanguage(navLang);
                    setLanguageState(initial);
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('language', initial);
                    }
                    hasInitialized.current = true;
                }
            } catch (_) {
                if (!hasInitialized.current) {
                    setLanguageState(DEFAULT_LANGUAGE);
                    hasInitialized.current = true;
                }
            }
        };

        initializeLanguage();
    }, [user?.id]);

    const setLanguage = useCallback((lang: string) => {
        setLanguageState(lang);
        try {
            if (typeof window !== 'undefined') {
                localStorage.setItem('language', lang);
            }
        } catch (_) {
            // ignore persistence errors
        }
    }, []);

    const value = { language, setLanguage };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
